import type { Entry } from "contentful";
import { SITE_URL } from "@/constants/siteUrl";
import { normalizePath } from "@/lib/redirects/normalize";
import type { TypeNavItemSkeleton } from "@/types/contentful/generated";
import { stripZeroWidth } from "@/utils/ContentfulHelper";
import { isEntryOfContentType } from "./guards";

export type NavItemEntry = Entry<TypeNavItemSkeleton, undefined, string>;

export function isNavItem(value: unknown): value is NavItemEntry {
  return isEntryOfContentType<NavItemEntry>(value, "navItem");
}

export function getNavItemChildren(item: NavItemEntry): NavItemEntry[] {
  return (item.fields.children ?? []).filter(isNavItem);
}

/** True when a value is an absolute (any URI scheme) or protocol-relative (`//host`) URL. */
export function isAbsoluteHref(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(value) || /^\/\/[^/]/.test(value);
}

// Schemes safe to render as a link target. CMS-authored destinations with any
// other scheme (javascript:, data:, vbscript:, …) are dropped so they can never
// become an executable href — mirrors the redirect guard in checkRedirect.ts.
const SAFE_ABSOLUTE_HREF = /^(https?:|mailto:|tel:|\/\/)/i;

/**
 * Resolves a raw destination value to an href. Safe full links (http(s),
 * mailto, tel, protocol-relative) pass through verbatim; unsafe schemes are
 * dropped to `""`; a bare slug or rooted path becomes a canonical, slashless
 * internal route.
 */
export function navItemHref(destination: string | undefined): string {
  // Contentful's preview client injects zero-width markers into string fields for
  // Live Preview source maps; left in the href they URL-encode as %E2%80%8B and 404.
  const value = stripZeroWidth(destination).trim();
  if (!value) return "";
  if (isAbsoluteHref(value)) return SAFE_ABSOLUTE_HREF.test(value) ? value : "";
  return normalizePath(`/${value.replace(/^\/+/, "")}`);
}

/**
 * Reads a nav item's destination, preferring the new `destinationURL` field and
 * falling back to a legacy `slug` field during the migration (an entry may carry
 * either or both).
 *
 * TODO(remove the slug fallback once all navItem entries are backfilled to destinationURL).
 */
export function navItemDestination(item: NavItemEntry): string {
  // `slug` is a transitional field absent from the generated type.
  const { destinationURL, slug } = item.fields as { destinationURL?: string; slug?: string };
  return destinationURL?.trim() || slug?.trim() || "";
}

/** Resolves a nav item directly to an href. */
export function navItemUrl(item: NavItemEntry): string {
  return navItemHref(navItemDestination(item));
}

/** Resolves a nav item to an absolute URL, leaving external links untouched. */
export function navItemAbsoluteUrl(item: NavItemEntry): string {
  const href = navItemUrl(item);
  if (!href) return SITE_URL;
  return isAbsoluteHref(href) ? href : `${SITE_URL}${href}`;
}

export function findNavItemPath(menu: NavItemEntry[], targetId: string): NavItemEntry[] {
  for (const node of menu) {
    if (node.sys.id === targetId) return [node];
    const childPath = findNavItemPath(getNavItemChildren(node), targetId);
    if (childPath.length > 0) return [node, ...childPath];
  }
  return [];
}
